import { Component, OnDestroy, OnInit } from '@angular/core';
import { PortfolioAnalysisService } from '../../../service/backend/portfolio-analysis.service';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, firstValueFrom, forkJoin, from, Subject } from 'rxjs';
import { AuthService } from '../../../../auth/service/auth.service';
import { OrganizationsService } from '../../../../organization/components/service/organizations.service';
import { Organization } from '../../../../organization/dto/Organization';
import { LocalDate } from '../../../../../core/pojo/date-variable';
import { MetricDataType, MetricSign } from '../../../../recovery/component/recovery-metrics-panel/entity/DefaultMetric';
import { skeletonAnimations } from '../../../../../util/animations/skeleton-animations';
import { cloneDeep } from 'lodash';
import { document } from "ngx-bootstrap/utils";
import { CompanyBackendService } from "../../../../pp/service/company-backend.service";
import {
  CompanyMiniDto,
  CompanyPortfolioAnalysisDto,
} from "../../../../recovery/component/recovery-company/dto/CompanyWithOrgDto";
import { NotificationsService } from "../../../../../core/service/notifications.service";
import { isRowValueEmpty, MetricDataRole } from '../../../dto/MetricData';
import {
  KpiAnalysisChartSetUp,
  MetricPortfolioAnalysisWrapper,
  MetricPortfolioAnalysisWrapperCompany,
  MetricPortfolioAnalysisWrapperEntryDTO,
  MetricPortfolioAnalysisWrapperGrouping,
} from "../../../dto/MetricPortfolioAnalysis";
import { MetricGroup } from "../../../dto/MetricGroup";
import { KpiBusinessService } from "../../../service/backend/kpi-business.service";
import { PortfolioAnalysisAllMetricsTemplate } from "../charts/PortfolioAnalysisAllMetricsTemplate";
import { ActivatedRoute, Router } from "@angular/router";
import { getKpiCompanyMetricsUrl } from "../../../module/kpi-module-routing.module";
import { ExcelService } from "../../../../excel";
import { ExcelTemplateName } from "../../../../excel/interfaces";
import { UUID } from "../../../../../core/pojo/id-variable";
import { throwErrorOnEmpty } from "../../../../../util/error/pipe-errors";
import { KpiModalDocumentCrudWrapper } from "../../../dto/box/box-kpi-dtos";
import { ToolTipSettings } from "../../../../shared/component/tooltip/tooltip.util";
import { TooltipKPIPortfolioAnalysisDocumentTableSettings } from "../../../service/internal/documents-helper.service";
import {
  CloseKpiMetricDocumentModalUpdateOutput,
} from "../../../modals/kpi-modal-document-crud/kpi-modal-document-crud.component";
import { ShowGroupingWrapper } from "../../../service/internal/track-untrack-grouping-helper";
import { DateService } from "../../../../../util/common/date-service/date.service";

@Component({
  selector: 'app-kpi-portfolio-analysis',
  templateUrl: './kpi-portfolio-analysis.component.html',
  styleUrls: ['./kpi-portfolio-analysis.component.scss'],
  animations: [skeletonAnimations],
})
export class KpiPortfolioAnalysisComponent implements OnInit, OnDestroy {
  private _destroyStream = new Subject<void>();
  protected readonly MetricDataType = MetricDataType;
  protected readonly Number = Number;
  protected readonly MetricSign = MetricSign;
  protected readonly MetricDataRole = MetricDataRole;
  protected readonly timeFrameHelper = new TimeFrameHelper();
  readonly MAX_METRIC_NAME_LENGTH = 22;

  selected = new Selected();

  wrapper: MetricPortfolioAnalysisWrapperGrouping;

  allMetricGroups: MetricGroup[];
  companies: CompanyMiniDto[];

  documentCrudWrapper: MetricDocumentUpdate = {
    wrapper: null,
    metricDataUUID: null,
    tracked: false,
  };

  isAnalysisProvided: boolean;
  dynamicHeaders: MetricPortfolioAnalysisWrapperEntryDTO[];
  sort = new Sort();
  isAdmin = false;
  show = new Show();
  chartSetUp = new KpiAnalysisChartSetUp();

  constructor(private authService: AuthService,
    private orgService: OrganizationsService,
    private metricsService: KpiBusinessService,
    private notificationService: NotificationsService,
    private router: Router,
    private route: ActivatedRoute,
    private companyService: CompanyBackendService,
    private portfolioAnalysisService: PortfolioAnalysisService,
    private excelService: ExcelService) {
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async selectTimeFrame(timeFrame: KpiAnalysisTimeFrame): Promise<void> {
    this.selected.timeFrame = timeFrame;
    await firstValueFrom(
      this.authService.userInfo.pipe(
        switchMap((user) =>
          this.portfolioAnalysisService.putTimePeriod(this.selected.org.uuid, this.selected.timeFrame, user.id)),
        catchError(() => EMPTY),
        takeUntil(this._destroyStream),
      ));
    await this.getWrapper();
  }

  showToolTipForStringMetric(id: string, text: string): void {
    this.show.tooltipForStringMetrics = {
      show: true,
      coordinates: document.getElementById(id).getBoundingClientRect(),
      text: text,
    };
  }

  activateDocumentsToolTip(id: string, text: string, entry: MetricPortfolioAnalysisWrapperEntryDTO): void {
    this.show.tooltipForDocuments = {
      show: true,
      expectedPeriod: entry.period.isMatchingPeriod ? null : entry.period.expected,
      providedPeriod: entry.period.isMatchingPeriod ? null : entry.period.provided,
      selectedFiles: text.split('\n').slice(1),
      title: text,
      coordinates: document.getElementById(id).getBoundingClientRect(),
    };
  }

  hideDocumentsToolTip(): void {
    this.show.tooltipForDocuments = {
      show: false,
      expectedPeriod: null,
      providedPeriod: null,
      selectedFiles: [],
    };
  }

  showTooltipForMissingPeriods(id: string, entry: MetricPortfolioAnalysisWrapperEntryDTO): void {
    if (!entry) {
      this.show.tooltipForMissingPeriods = null;
      return;
    }
    this.show.tooltipForMissingPeriods = {
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
      expected: entry.period.expected,
      provided: entry.period.provided,
    };
  }

  async closeCrudModal(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      await this.getWrapper();
    }
    this.show.crudModal = false;
  }

  exportExcel(type: 'Actual' | 'Budget' | 'Analysis'): void {
    if ((type === "Actual" || type === 'Budget') && (!this.allMetricGroups || !this.allMetricGroups.length)) {
      this.notifyInsufficientMetricGroupsForExcel();
      return;
    }
    switch (type) {
      case 'Actual':
        const isActualSuccessful = new PortfolioAnalysisAllMetricsTemplate().export(this.allMetricGroups, this.selected.org.name, MetricDataRole.ACTUAL, this.companies);
        if (!isActualSuccessful) {
          this.notifyInsufficientMetricDataForExcel(MetricDataRole.ACTUAL);
        }
        break;
      case 'Budget':
        const isBudgetSuccessful = new PortfolioAnalysisAllMetricsTemplate().export(this.allMetricGroups, this.selected.org.name, MetricDataRole.BUDGET, this.companies);
        if (!isBudgetSuccessful) {
          this.notifyInsufficientMetricDataForExcel(MetricDataRole.BUDGET);
        }
        break;
      case 'Analysis':
        break;
    }
  }

  async goToCompanyMetrics(company: CompanyPortfolioAnalysisDto): Promise<void> {
    await this.router.navigate(
      [getKpiCompanyMetricsUrl(company.uuid)],
      { queryParams: this.route.snapshot.queryParams });
  }

  exportTable(): void {
    const fileName = 'Org_Entire_Portfolio_KPIs.xlsx';
    const exportData: MetricPortfolioAnalysisWrapperCompanyExportExcel = {
      headers: this.dynamicHeaders,
      data: this.wrapper.all,
    };
    this.excelService.exportByTemplate(exportData, fileName, ExcelTemplateName.KpiPortfolioAnalysis);
  }

  /**
   * Open chart modal and collect all necessary data for backend request.
   *
   * @param metricGroupName name of the group (eg. ARR)
   * @param metricName metric name (eg. Starting, Ending, New)
   * @param companyUUID UUID of selected company.
   * @param role Actual or Budget role
   */
  openChartModal(metricGroupName: string, metricName: string, role: string, companyUUID?: string, companyName?: string): void {
    this.chartSetUp = {
      isTotal: !companyUUID,
      timeFrame: this.selected.timeFrame,
      orgId: this.selected.org.id,
      companyUUID: companyUUID,
      companyName: companyName,
      metricGroupName: metricGroupName,
      metricName: metricName,
      role: role,
    };
    this.show.chartModal = true;
  }

  setKpiShowIconSettings(trIndex: number, tdIndex: number, headerIndex: number): void {
    this.show.chartIcon = {
      trIndex,
      tdIndex,
      headerIndex,
    };
  }

  showMetricRoleTooltip(header: MetricPortfolioAnalysisWrapperEntryDTO, id: string): void {
    this.show.tooltipMetricRole = {
      coordinates: document.getElementById(id).getBoundingClientRect(),
      title: `${header.role} data as of ${DateService.formatShortDate(header.period.expected)}`,
    };
  }


  async updateTrackStatus(companyUUID: UUID, isTracked: boolean): Promise<void> {
    await firstValueFrom(this.portfolioAnalysisService.updateIsTrackedStatus(companyUUID, isTracked)
      .pipe(
        takeUntil(this._destroyStream),
        throwErrorOnEmpty(undefined, 'Could not update track status in kpi metrics')
      ));

    await this.getWrapper();
  }

  showMetricHeaderTooltip(header: MetricPortfolioAnalysisWrapperEntryDTO, id: string): void {
    if (header.metricGroupName.length < this.MAX_METRIC_NAME_LENGTH && header.metricName.length < this.MAX_METRIC_NAME_LENGTH) {
      return;
    }

    this.show.tooltipHeaderMetricName = {
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
      tooltipText: header.metricGroupName,
      tooltipTitle: header.metricName,
    };
  }

  showDocuments(entry: MetricPortfolioAnalysisWrapperEntryDTO, companyUUID: UUID, tracked: boolean): void {
    const boxFolderId = entry.data.rawValue.split('\n')[0];
    this.documentCrudWrapper.metricDataUUID = entry.data.uuid;
    this.documentCrudWrapper.tracked = tracked;
    this.documentCrudWrapper.wrapper = new KpiModalDocumentCrudWrapper(
      companyUUID,
      entry.data.uuid,
      boxFolderId,
      entry.period.expected,
      entry.metricName,
      false,
      false,
    );
  }

  updateRowValueFromMetricDocumentAndClose(update: CloseKpiMetricDocumentModalUpdateOutput): void {
    if (this.documentCrudWrapper.tracked) {
      this.updateCompanyEntries(this.wrapper.tracked.companies, update.rowValue);
    } else {
      this.updateCompanyEntries(this.wrapper.untracked.companies, update.rowValue);
    }
    if (update.close) {
      this.documentCrudWrapper.wrapper = null;
      this.documentCrudWrapper.metricDataUUID = null;
    }
  }

  private notifyInsufficientMetricDataForExcel(role: MetricDataRole): void {
    this.notificationService.warning({
      title: 'Insufficient data',
      message: `There are no companies with ${role} metric data`,
    });
  }

  private notifyInsufficientMetricGroupsForExcel(): void {
    this.notificationService.warning({
      title: 'Insufficient data',
      message: `There are no Metric Groups for this organization`,
    });
  }

  private initSubscriptions(): void {
    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        if (!userType) {
          return;
        }
        this.isAdmin = userType === 'admin';
      });

    this.orgService.selected$
      .pipe(
        filter((org) => Boolean(org)),
        tap((org) => this.selected.org = org),
        tap(() => {
          this.wrapper = this.isAnalysisProvided = this.allMetricGroups = undefined;
        }),
        switchMap(() => this.getTimePeriod()),
        switchMap(() => forkJoin([
          from(this.getFullMetricGroups()),
          this.getWrapper(),
          from(this.getCompanies()),
        ])),
        takeUntil(this._destroyStream))
      .subscribe();
  }

  private async getCompanies(): Promise<void> {
    this.companies = await this.companyService.getMini(this.selected.org.uuid);
  }

  private async getFullMetricGroups(): Promise<void> {
    this.allMetricGroups = await firstValueFrom(this.metricsService.getFullMetricGroups(this.selected.org.uuid));
  }

  private async getTimePeriod(): Promise<void> {
    this.selected.timeFrame = await firstValueFrom(this.authService.userInfo.pipe(
      switchMap((info) =>
        this.portfolioAnalysisService.getTimePeriod(this.selected.org.uuid, info.id)
      ),
      map((timeFrame) => timeFrame ?? 'PAST6MONTHS'),
      takeUntil(this._destroyStream),
    ));
  }

  private async getWrapper(): Promise<void> {
    this.wrapper = null;
    this.isAnalysisProvided = await firstValueFrom(this.portfolioAnalysisService
      .getIsAnalysisProvided(this.selected.org.uuid).pipe(takeUntil(this._destroyStream)));
    if (!this.isAnalysisProvided) {
      return;
    }
    try {
      const wrapper = await firstValueFrom(
        this.portfolioAnalysisService.getWrapper(this.selected.org.uuid, this.selected.timeFrame)
          .pipe(takeUntil(this._destroyStream)));

      if (!wrapper.companies.length) {
        this.isAnalysisProvided = false;
        return;
      }
      this.setWrapperWithTableExtras(wrapper);
    } catch (e) {
      this.wrapper = null;
    }
  }

  private setWrapperWithTableExtras(wrapper: MetricPortfolioAnalysisWrapper): void {
    this.sort.byMetric = wrapper.companies.map((_, i) =>
      function (c: MetricPortfolioAnalysisWrapperCompany): number {
        // if company has at least one entry, then it can be used for sorting.
        if (c.entries.some((entry) => !isRowValueEmpty(entry.data.rawValue))) {
          return Number(c.entries[i].data.rawValue ?? 0) * (c.entries[i].data.sign === MetricSign.NEGATIVE ? -1 : 1);
        }
        // company has no entries, therefore it should be always at the bottom of the list.
        return Number.MIN_VALUE;
      });
    this.dynamicHeaders = cloneDeep(wrapper.companies[0].entries);
    this.wrapper = this.divideWrapperIntoGrouping(wrapper);
    this.show.grouping.tracked.amount = this.wrapper.tracked.companies.length;
    this.show.grouping.untracked.amount = this.wrapper.untracked.companies.length;
  }

  private divideWrapperIntoGrouping(wrapper: MetricPortfolioAnalysisWrapper): MetricPortfolioAnalysisWrapperGrouping {
    const tracked = cloneDeep(wrapper);
    tracked.companies = tracked.companies.filter((companyWrapper) => companyWrapper.company.isKpiPortfolioTracked);
    const untracked = cloneDeep(wrapper);
    untracked.companies = untracked.companies.filter((companyWrapper) => !companyWrapper.company.isKpiPortfolioTracked);
    return {
      all: wrapper,
      tracked,
      untracked,
    };
  }

  private updateCompanyEntries(companies: MetricPortfolioAnalysisWrapperCompany[], newValue: string): void {
    companies.forEach((company) => {
      company.entries.forEach((entry) => {
        if (entry.data?.uuid === this.documentCrudWrapper.metricDataUUID) {
          entry.data.rawValue = newValue;
        }
      });
    });
  }
}

class Show {
  crudModal = false;
  chartModal = false;
  chartIcon = new ShowKpiChartIcon();
  grouping = new ShowGroupingWrapper();
  tooltipForStringMetrics: ToolTipSettings = { show: false };
  tooltipForDocuments: TooltipKPIPortfolioAnalysisDocumentTableSettings = {
    show: false,
    selectedFiles: [],
  };

  tooltipForMissingPeriods: {
    tooltipCoordinates: DOMRect;
    expected: LocalDate;
    provided: LocalDate;
  };

  tooltipHeaderMetricName: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
    tooltipTitle: string;
  };

  tooltipMetricRole: ToolTipSettings;
}

class ShowKpiChartIcon {
  trIndex: number;
  tdIndex: number;
  headerIndex: number;
}

class Selected {
  org: Organization;
  timeFrame: KpiAnalysisTimeFrame;
}

class Sort {
  byMetric: ((x: MetricPortfolioAnalysisWrapperCompany) => number)[];
  byCompany = (x: MetricPortfolioAnalysisWrapperCompany): string => x.company.name;
}

export interface MetricPortfolioAnalysisWrapperCompanyExportExcel {
  headers: MetricPortfolioAnalysisWrapperEntryDTO[];
  data: MetricPortfolioAnalysisWrapper;
}

export type KpiAnalysisTimeFrame = 'SELECTED' | 'PAST3MONTHS' | 'PAST6MONTHS' | 'PAST_YEAR' | 'ALL_TIME'

class TimeFrameHelper {
  map = new Map<string, KpiAnalysisTimeFrame>(
    [
      ['Selected date', 'SELECTED'],
      ['Past 3 months', 'PAST3MONTHS'],
      ['Past 6 months', 'PAST6MONTHS'],
      ['Past year', 'PAST_YEAR'],
      ['All time', 'ALL_TIME'],
    ]);

  reversedMap: Map<KpiAnalysisTimeFrame, string> = new Map();

  constructor() {
    this.map.forEach((value, key) => {
      this.reversedMap.set(value, key);
    });
  }
}

interface MetricDocumentUpdate {
  wrapper: KpiModalDocumentCrudWrapper;
  metricDataUUID: string;
  tracked: boolean;
}

